import { DateTime } from 'luxon';

import {
  ShipmentDeliveryEvents,
  ShipmentEvent,
  ShipmentEventType,
  ShipmentOriginEvents,
  ShipmentServiceIncident,
  ShipmentStop,
  TenderRequest,
  TenderResponse,
  TimelineItem,
} from '@/model';

const uncomparableEventTypes = [
  ShipmentEventType.INFO,
  ShipmentEventType.UNKNOWN,
];

function extractStopSeq(
  item: TimelineItem,
  shipmentStops: ShipmentStop[],
): number | undefined {
  const relatedStopId: number | undefined =
    item instanceof ShipmentEvent || item instanceof ShipmentServiceIncident
      ? item.relatedStopId
      : undefined;

  const relatedStop = shipmentStops
    .filter((stop) => stop.id && stop.sequenceNumber)
    .find((stop) => stop.id === relatedStopId);

  return relatedStop?.sequenceNumber;
}

function buildStopSequenceComparator(shipmentStops: ShipmentStop[]) {
  return function compareStopSequence(
    a: TimelineItem,
    b: TimelineItem,
  ): number {
    const aStopSeq = extractStopSeq(a, shipmentStops);
    const bStopSeq = extractStopSeq(b, shipmentStops);
    if (!aStopSeq || !bStopSeq) {
      return 0;
    }
    return aStopSeq - bStopSeq;
  };
}

const extractDate = (item: TimelineItem): DateTime | undefined => {
  if (item instanceof ShipmentEvent) {
    return (
      item.actualDate ||
      item.expectedDateRange?.start ||
      item.expectedDateRange?.end
    );
  } else if (item instanceof ShipmentServiceIncident) {
    return item.actualDate;
  } else if (item instanceof TenderRequest) {
    return item.createdDate;
  } else if (item instanceof TenderResponse) {
    return item.responseDate;
  }
};

function compareDate(a: TimelineItem, b: TimelineItem): number {
  const comparableDateA = extractDate(a);
  const comparableDateB = extractDate(b);

  if (comparableDateA && comparableDateB) {
    return comparableDateA < comparableDateB
      ? -1
      : comparableDateA > comparableDateB
        ? 1
        : 0;
  }
  if (!comparableDateA && comparableDateB) {
    return 1;
  } else if (comparableDateA && !comparableDateB) {
    return -1;
  } else {
    return 0;
  }
}

function compareOriginEventType(
  a: ShipmentEventType,
  b: ShipmentEventType,
): number {
  const aPosition = ShipmentOriginEvents.indexOf(a);
  const bPosition = ShipmentOriginEvents.indexOf(b);
  const aIsOrigin = aPosition !== -1;
  const bIsOrigin = bPosition !== -1;
  if (aIsOrigin && !bIsOrigin) {
    return -1;
  } else if (!aIsOrigin && bIsOrigin) {
    return 1;
  } else {
    return aPosition - bPosition;
  }
}

function compareDeliveryEventType(
  a: ShipmentEventType,
  b: ShipmentEventType,
): number {
  const aPosition = ShipmentDeliveryEvents.indexOf(a);
  const bPosition = ShipmentDeliveryEvents.indexOf(b);
  const aIsDelivery = aPosition !== -1;
  const bIsDelivery = bPosition !== -1;
  if (aIsDelivery && !bIsDelivery) {
    return 1;
  } else if (!aIsDelivery && bIsDelivery) {
    return -1;
  } else {
    return aPosition - bPosition;
  }
}

const compareEventType = (a: TimelineItem, b: TimelineItem): number => {
  if (a instanceof ShipmentEvent && b instanceof ShipmentEvent) {
    const aEventType = a.eventType ?? ShipmentEventType.UNKNOWN;
    const bEventType = b.eventType ?? ShipmentEventType.UNKNOWN;

    if (uncomparableEventTypes.includes(aEventType)) {
      return 0;
    }
    if (uncomparableEventTypes.includes(bEventType)) {
      return 0;
    }

    return (
      compareOriginEventType(aEventType, bEventType) ||
      compareDeliveryEventType(aEventType, bEventType)
    );
  }
  return 0;
};

/**
 * Attempt to merge the service incident into the timeline items
 * @returns `true` if the service incident was merged, `false` otherwise
 */
function mergeServiceIncidentByDate(
  serviceIncident: ShipmentServiceIncident,
  timeLineItems: TimelineItem[],
): boolean {
  const serviceIncidentDate = extractDate(serviceIncident);
  if (!serviceIncidentDate) {
    return false;
  }

  for (const [i, currItem] of timeLineItems.entries()) {
    const prevItem = timeLineItems[i - 1];
    const prevItemDate = extractDate(prevItem);
    const currItemDate = extractDate(currItem);
    if (!prevItemDate || !currItemDate) {
      continue;
    }

    if (
      serviceIncidentDate >= prevItemDate &&
      serviceIncidentDate <= currItemDate
    ) {
      timeLineItems.splice(i, 0, serviceIncident);
      return true;
    }
  }

  return false;
}

function toShipmentTimeLineItems(
  shipmentStops: ShipmentStop[],
  shipmentEvents: ShipmentEvent[],
  shipmentServiceIncidents: ShipmentServiceIncident[],
  tenderRequests: TenderRequest[],
): TimelineItem[] {
  const compareStopSequence = buildStopSequenceComparator(shipmentStops);

  const tenderRequestsSorted = [...tenderRequests]
    .sort(compareDate)
    .flatMap((tenderRequest) => {
      if (tenderRequest.tenderResponse) {
        return [tenderRequest, tenderRequest.tenderResponse];
      }
      return [tenderRequest];
    });

  const shipmentEventsSorted = [...shipmentEvents].sort(
    (a, b) =>
      compareStopSequence(a, b) || compareEventType(a, b) || compareDate(a, b),
  );

  const shipmentTimeLineItems: TimelineItem[] = [
    ...tenderRequestsSorted,
    ...shipmentEventsSorted,
  ];

  for (const serviceIncident of shipmentServiceIncidents) {
    const wasMergedByDate = mergeServiceIncidentByDate(
      serviceIncident,
      shipmentTimeLineItems,
    );
    if (!wasMergedByDate) {
      shipmentTimeLineItems.push(serviceIncident);
    }
  }

  return shipmentTimeLineItems;
}

type ShipmentTimeLineItemsUtil = {
  toShipmentTimeLineItems: (
    shipmentStops: ShipmentStop[],
    shipmentEvents: ShipmentEvent[],
    shipmentServiceIncidents: ShipmentServiceIncident[],
    tenderRequests: TenderRequest[],
  ) => TimelineItem[];
};

export function useShipmentTimeLineItems(): ShipmentTimeLineItemsUtil {
  return { toShipmentTimeLineItems };
}
